import { forwardRef, useId, useMemo } from "react";

import { cn } from "@/lib/utils";

export const RatingEnum = {
  DISLIKE: "DISLIKE",
  LIKE: "LIKE",
  LOVE: "LOVE",
} as const;
export type RatingEnum = (typeof RatingEnum)[keyof typeof RatingEnum];

export interface CommunityScorePercentageType {
  percentage: number;
  rating: RatingEnum;
}

interface CommunityScoreWheelProps extends React.ComponentProps<"svg"> {
  scores: CommunityScorePercentageType[];
}

export default forwardRef<SVGSVGElement, CommunityScoreWheelProps>(
  function CommunityScoreWheel({ className, scores, ...props }, ref) {
    const normalizedScores = useMemo(
      () =>
        scores.reduce(
          (accum, { percentage, rating }) => {
            accum[rating] = percentage;
            return accum;
          },
          {} as NodeJS.Dict<number>
        ),
      [scores]
    );

    const id = useId();

    // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: its fine
    const svg = useMemo((): React.ReactNode => {
      const space = 7;
      const rawDislikes = Math.floor(normalizedScores[RatingEnum.DISLIKE] || 0);
      const dislikes = rawDislikes >= 3 ? Math.max(rawDislikes, space) : 0;
      const loves = normalizedScores[RatingEnum.LOVE] || 0;
      const likes = 100 - loves - dislikes;

      const singleCircle = (
        stroke: string,
        extraProps: React.SVGProps<SVGCircleElement> = {}
      ) => (
        <circle
          cx="21"
          cy="21"
          fill="none"
          r="15.91549430918954"
          stroke={stroke}
          strokeWidth="5"
          {...extraProps}
        />
      );

      if (dislikes > 100 - space) {
        return singleCircle("#41c9fa");
      }

      if (likes === 100) {
        return singleCircle("#ffc850");
      }

      if (loves === 100) {
        return singleCircle("#ff5c00");
      }

      const negativeSegment = dislikes;
      const positiveSegment = 100 - negativeSegment;

      const negativeCircle =
        dislikes >= space
          ? singleCircle("#41c9fa", {
              strokeDasharray: `${negativeSegment - space} ${positiveSegment + space}`,
              strokeDashoffset: negativeSegment,
              strokeLinecap: "round",
            })
          : null;

      const mask = (
        <defs>
          <mask id={id}>
            {singleCircle(
              "#fff",
              negativeCircle
                ? {
                    strokeDasharray: `${positiveSegment - space} ${negativeSegment + space}`,
                    strokeLinecap: "round",
                  }
                : {}
            )}
          </mask>
        </defs>
      );

      let background = "";

      if (loves === 0) {
        background = "#ffc850";
      } else if (likes === 0) {
        background = "#ff5c00";
      } else {
        const gradient: string[] = [];
        const transition = 7;
        const angle = 81;

        if (negativeCircle) {
          const safeLoves = Math.min(loves, 100 - transition);

          gradient.push(`from ${angle}deg`);
          gradient.push(`#ff5c00 ${safeLoves}%`);
          gradient.push(`#ffc850 ${safeLoves + transition}%`);
        } else {
          const safeLoves = Math.min(loves, 100 - transition * 2);

          gradient.push(`from ${angle + (transition / 100) * 360}deg`);
          gradient.push(`#ff5c00 ${safeLoves}%`);
          gradient.push(`#ffc850 ${safeLoves + transition}%`);
          gradient.push(`#ffc850 ${100 - transition}%`);
          gradient.push("#ff5c00 100%");
        }

        background = `conic-gradient(${gradient.join(", ")})`;
      }

      return (
        <>
          {mask}
          <foreignObject
            height="42"
            mask={`url(#${id})`}
            width="42"
            x="0"
            y="0"
          >
            <div className="size-full" style={{ background }} />
          </foreignObject>
          {negativeCircle}
        </>
      );
    }, [normalizedScores, id]);

    return (
      <svg
        aria-hidden="true"
        className={cn("size-[21px]", className)}
        ref={ref}
        viewBox="0 0 42 42"
        xmlns="http://www.w3.org/2000/svg"
        {...props}
      >
        <g transform="rotate(270 21 21)">{svg}</g>
      </svg>
    );
  }
);
